import { useState, useEffect, useCallback, useRef } from "react";
import { throttle } from "lodash";

export const useFullPageScroll = (totalSections) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1200);
  const touchStart = useRef(0);
  const isScrolling = useRef(false);

  // 💡 핵심 1: 창 크기를 실시간으로 감지하여 데스크탑 여부 판단 & 바디 스크롤 제어
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth > 1200;
      setIsDesktop(desktop);
      
      // 1200px 이하로 줄어들면 브라우저 바디 전체의 스크롤 제한을 강제 해제합니다!
      document.body.style.overflow = desktop ? "hidden" : "auto";
    };

    // 최초 실행
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      document.body.style.overflow = "auto"; // 컴포넌트가 사라질 때 원상복구
    };
  }, []);

  const movePage = useCallback(
    (direction) => {
      if (!isDesktop || isScrolling.current) return;

      if (direction === "down" && currentPage < totalSections - 1) {
        isScrolling.current = true;
        setCurrentPage((prev) => prev + 1);
      } else if (direction === "up" && currentPage > 0) {
        isScrolling.current = true;
        setCurrentPage((prev) => prev - 1);
      }

      setTimeout(() => {
        isScrolling.current = false;
      }, 1000);
    },
    [totalSections, currentPage, isDesktop]
  );

  const handleScroll = useCallback(
    throttle(
      (e) => {
        if (!isDesktop) return;

        if (e.type === "wheel") {
          if (Math.abs(e.deltaY) < 10) return;
          if (e.deltaY > 0) movePage("down");
          else movePage("up");
        }

        if (e.type === "touchend") {
          const touchEnd = e.changedTouches.pageY;
          const diff = touchStart.current - touchEnd;

          if (Math.abs(diff) > 50) {
            if (diff > 0) movePage("down");
            else movePage("up");
          }
        }
      },
      400,
      { leading: true, trailing: false }
    ),
    [movePage, isDesktop]
  );

  const handleTouchStart = useCallback((e) => {
    if (!isDesktop) return;
    touchStart.current = e.touches.pageY;
  }, [isDesktop]);

  useEffect(() => {
    // 💡 핵심 2: 1200px 이하(모바일/태블릿)일 때는 휠 이벤트를 아예 차단해버림! (순정 스크롤 보장)
    if (!isDesktop) return;

    const preventDefault = (e) => {
      if (e.cancelable) e.preventDefault();
    };

    window.addEventListener("wheel", preventDefault, { passive: false });
    window.addEventListener("touchmove", preventDefault, { passive: false });
    
    window.addEventListener("wheel", handleScroll);
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleScroll);

    return () => {
      window.removeEventListener("wheel", preventDefault);
      window.removeEventListener("touchmove", preventDefault);
      window.removeEventListener("wheel", handleScroll);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleScroll);
    };
  }, [handleScroll, handleTouchStart, isDesktop]);

  // isDesktop 상태도 밖으로 내보내서 Home.js에서 재활용합니다.
  return { currentPage, setCurrentPage, isDesktop };
};